import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button, CardContent } from '@material-ui/core';
import { matchPath, NavLink as RouterLink, useHistory, useLocation, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import CardHeader from '@material-ui/core/CardHeader';
import Card from '@material-ui/core/Card';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import { isEmpty } from 'lodash';
import Skeleton from '@material-ui/lab/Skeleton';
import { actions as LogEntriesActions } from '../../Ducks/LogEntries.duck';
import { actions as TasksActions } from '../../Ducks/Tasks.duck';
import { actions as ConnectionActions } from '../../Ducks/Connection.duck';
import { actions as SyncStatusActions } from '../../Ducks/SyncStatus.duck';
import LogsTable from '../../Components/LogsTable';
import TasksTable from '../../Components/TasksTable';
import {
  clearConnectionLogs,
  clearConnectionTasks,
  clearSelectedConnectionLogs,
  updateAllConnectionLogsStatus,
  updateConnection,
  updateSelectedConnectionLogsStatus,
} from '../../Services/ConnectionService';
import ChangeConnectionWizard from '../../Components/Change/ChangeConnectionWizard';
import SyncStatusTable from '../../Components/SyncStatusTable';
import { actions as AppActions } from '../../../App/Ducks/App.duck';
import { MESSAGE_SEVERITY_ERROR, MESSAGE_SEVERITY_SUCCESS } from '../../../../common/constants';
import { LOG_STATUS } from './logStatusConstants';
import { SearchInput } from '../../../../components';
import ConnectionDetailsCard from '../../Components/ConnectionDetailsCard';
import { retryTask } from '../../Services/TaskService';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  buttonElement: {
    marginRight: theme.spacing(1),
  },
  searchInput: {
    marginBottom: theme.spacing(1),
  },
  flex: {
    display: 'flex',
  },
  flexAlignRight: {
    marginLeft: 'auto',
    marginRight: '0',
  },
}));

const CustomRouterLink = forwardRef((props, ref) => (
  <div ref={ref} style={{ display: 'inline-block' }}>
    <RouterLink {...props} />
  </div>
));

const ConnectionDetailsPage = () => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { connectionId } = useParams();
  const [clearingTasks, setClearingTasks] = useState(false);
  const [clearingLogs, setClearingLogs] = useState(false);
  const [clearingSelectedLogs, setClearingSelectedLogs] = useState(false);
  const [modal2Open, setModal2Open] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [runButtonDisabled, setRunButtonDisabled] = useState(false);
  const [updatingAllLogsStatus, setUpdatingAllLogsStatus] = useState(false);
  const [updatingSelectedLogsStatus, setUpdatingSelectedLogsStatus] = useState(false);

  const { loading, item: connection } = useSelector(state => state.ConnectionReducer);
  const {
    page: pageLogs,
    pageSize: pageSizeLogs,
    loading: loadingLogs,
    items: logs,
    totalCount: totalCountLogs,
    logId: stateLogId,
    // canDownload,
    // checkingDownload,
  } = useSelector(state => state.LogEntriesReducer);
  const { page, pageSize, loading: loadingTasks, items: tasks, totalCount } = useSelector(state => state.TasksReducer);
  const { user } = useSelector(state => state.AppReducer);

  const { loading: loadingSyncStatusReport, syncStatusReport } = useSelector(state => state.SyncStatusReducer);

  const location = useLocation();

  const { logId: paramLogId } = useParams();
  let logId = null;
  let openedLog = null;
  if (stateLogId) {
    logId = stateLogId;
  } else if (matchPath(location.pathname, '/connection/:connectionId/log/:logId')) {
    logId = paramLogId;
  }

  logs.forEach(log => {
    if (logId === log['@id'].substring(log['@id'].lastIndexOf('/')).slice(1)) {
      openedLog = log;
    }
  });

  useEffect(() => {
    dispatch(ConnectionActions.requestData(connectionId));
  }, [dispatch]);

  const isUserSuperAdmin = useMemo(() => {
    return user.roles.includes('ROLE_SUPER_ADMIN');
  }, [user]);

  useEffect(() => {
    if (connection !== undefined && Object.keys(connection).length === 0 && !loading) {
      history.push('/not-found');
    } else if (connection !== undefined && Object.keys(connection).length > 0 && !loading) {
      dispatch(LogEntriesActions.setConnectionId(connectionId));
      dispatch(LogEntriesActions.requestData(connectionId));

      // dispatch(LogEntriesActions.checkCanDownloadMissingDetails());
      dispatch(TasksActions.setConnectionId(connectionId));
      dispatch(TasksActions.requestData(connectionId));
      dispatch(SyncStatusActions.setConnectionId(connectionId));
      dispatch(SyncStatusActions.requestData(connectionId));
    }
  }, [loading, connection, dispatch]);

  if (isEmpty(connection) && !loading) {
    history.push('/not-found');
    return null;
  }

  const handleClickChange = () => {
    setModal2Open(true);
  };

  const handleChangeConnection = async data => {
    await updateConnection(data);
    setModal2Open(false);
    window.location.reload();
  };

  const handleRunConnectionModule = async conn => {
    const payload = {
      connectionId: conn,
      parameters: {},
    };
    try {
      dispatch(ConnectionActions.forceRun(payload));
    } catch (e) {
      console.log(e);
    }
    dispatch(ConnectionActions.requestData(connectionId));
  };

  const handleClickResetSyncItems = async conn => {
    const payload = {
      connectionId: conn,
      parameters: {},
    };

    dispatch(ConnectionActions.resetSyncStatus(payload));

    await new Promise(resolve => setTimeout(resolve, 1000));

    dispatch(SyncStatusActions.requestData(connectionId));
  };

  const clearTasks = async force => {
    setClearingTasks(true);
    try {
      await clearConnectionTasks(connectionId, force);
    } catch (error) {
      alert('Failed to clear tasks');
    }
    dispatch(TasksActions.requestData(connectionId));
    setClearingTasks(false);
  };

  const clearLogs = async old => {
    setClearingLogs(true);
    try {
      await clearConnectionLogs(connectionId, old);
    } catch (error) {
      alert('Failed to clear logs');
    }
    dispatch(LogEntriesActions.requestData(connectionId));
    setClearingLogs(false);
  };

  const clearSelectedLogs = async () => {
    setClearingSelectedLogs(true);
    const logIds = logs.filter(item => item.selectedRow).map(item => item.mongoId);
    try {
      await clearSelectedConnectionLogs(connectionId, logIds);
    } catch (error) {
      setClearingSelectedLogs(false);
      dispatch(
        AppActions.displayMessage({
          severity: MESSAGE_SEVERITY_ERROR,
          message: 'connectionDetails.logEntries.selectedLogsClearError',
        }),
      );
    }
    dispatch(LogEntriesActions.requestData(connectionId));
    setClearingSelectedLogs(false);
  };

  const handleToggleLog = log => {
    dispatch(LogEntriesActions.toggleLog(log));
  };

  const handleToggleAllLogs = () => {
    dispatch(LogEntriesActions.toggleAllLogs(logs));
  };

  const handleClickUpdateAllLogsStatus = async status => {
    setUpdatingAllLogsStatus(true);
    try {
      await updateAllConnectionLogsStatus(connectionId, status);
    } catch (error) {
      setUpdatingAllLogsStatus(false);
      dispatch(
        AppActions.displayMessage({
          severity: MESSAGE_SEVERITY_ERROR,
          message: 'connectionDetails.logEntries.allLogsStatusUpdatedError',
        }),
      );
    }
    dispatch(LogEntriesActions.requestData(connectionId));
    setUpdatingAllLogsStatus(false);
  };

  const handleClickUpdateSelectedLogsStatus = async status => {
    setUpdatingSelectedLogsStatus(true);
    const logIds = logs.filter(item => item.selectedRow).map(item => item.mongoId);
    try {
      await updateSelectedConnectionLogsStatus(connectionId, status, logIds);
    } catch (error) {
      setUpdatingSelectedLogsStatus(false);
      dispatch(
        AppActions.displayMessage({
          severity: MESSAGE_SEVERITY_ERROR,
          message: 'connectionDetails.logEntries.selectedLogsStatusUpdatedError',
        }),
      );
    }
    dispatch(LogEntriesActions.requestData(connectionId));
    setUpdatingSelectedLogsStatus(false);
  };

  const retrieveSyncStatusReport = () => {
    dispatch(SyncStatusActions.requestData(connectionId));
  };

  const handlePageChange = (event, newPage) => {
    dispatch(TasksActions.changePage(newPage + 1));
  };

  const handleRowsPerPageChange = event => {
    dispatch(TasksActions.setPageSize(event.target.value));
  };

  const handlePageChangeLogs = (event, newPage) => {
    dispatch(LogEntriesActions.changePage(newPage + 1));
  };

  const handleRowsPerPageChangeLogs = event => {
    dispatch(LogEntriesActions.setPageSize(event.target.value));
  };

  const handleSearch = event => {
    dispatch(LogEntriesActions.searchLogs(event.target.value));
  };

  // const downloadLogs = async () => {
  //   try {
  //     const response = await downloadMissingDetails(connectionId);
  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', response.headers['x-filename']);
  //     document.body.appendChild(link);
  //     link.click();
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  const connectionUsesPackage = packageType => {
    if (connection === undefined || loading) {
      return false;
    }

    let usePackage = false;
    connection.packages.forEach(packageObject => {
      if (packageObject.packageType === packageType) {
        usePackage = true;
      }
    });

    return usePackage;
  };

  const handleSetLogId = logIdToSet => {
    dispatch(LogEntriesActions.setLogId(logIdToSet));
  };

  const handleOpenLog = log => {
    let openedLogId = null;

    if (log !== null) {
      openedLogId = log['@id'].substring(log['@id'].lastIndexOf('/')).slice(1);
    }

    handleSetLogId(openedLogId);

    if (openedLogId !== null) {
      history.push(`/connection/${connectionId}/log/${openedLogId}`);
    } else {
      history.push(`/connection/${connectionId}`);
    }
  };

  const handleRetryTask = async taskId => {
    const response = await retryTask(connectionId, taskId);

    dispatch(
      AppActions.displayMessage({
        severity: response.status === 202 ? MESSAGE_SEVERITY_SUCCESS : MESSAGE_SEVERITY_ERROR,
        message: 'connection.retryTask',
      }),
    );

    dispatch(TasksActions.requestData(connectionId));
  };

  const redirectToSupportPortal = () => {
    const supportPortalUrl = 'https://databrydge.atlassian.net/servicedesk/customer/portal/1/group/1/create/1';
    const supportPortalConnectionUrlFieldId = 'customfield_10193';
    const supportPortalPackagesFieldId = 'customfield_10192';

    const currentUrl = window.location.href;

    const packages = [];
    connection.packages.forEach(connectionPackage => {
      packages.push(connectionPackage.packageType);
    });
    const packageString = packages.join(' - ');

    const url = encodeURI(`${supportPortalUrl}?${supportPortalConnectionUrlFieldId}=${currentUrl}&${supportPortalPackagesFieldId}=${packageString}`);
    window.open(url, '_blank');
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <Typography variant="h1">
            {!loading && connection.connectionName ? connection.connectionName : <Skeleton />}
          </Typography>
        </Grid>
        <Grid item xs={12} className={classes.flex}>
          <Button
            color="primary"
            variant="contained"
            className={classes.buttonElement}
            onClick={() => handleClickChange(connection)}
          >
            <FormattedMessage id="button.change" />
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.buttonElement}
            disabled={runButtonDisabled}
            onClick={() => handleRunConnectionModule(connectionId)}
          >
            <FormattedMessage id="button.run" />
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.buttonElement}
            component={CustomRouterLink}
            to={`/connection/${connectionId}/insight`}
            exact={page.exact || false}
          >
            <FormattedMessage id="button.insight" />
          </Button>
          {isUserSuperAdmin && (
            <Button
              color="primary"
              variant="contained"
              className={classes.buttonElement}
              component={CustomRouterLink}
              to={`/connection/${connectionId}/curls`}
              exact={page.exact || false}
            >
              <FormattedMessage id="button.curls" />
            </Button>
          )}
          {connectionUsesPackage('payment-toggle') && (
            <Button
              color="primary"
              variant="contained"
              className={classes.buttonElement}
              component={CustomRouterLink}
              to={`/connection/payment-toggle/${connectionId}/run-details`}
              exact={page.exact || false}
            >
              <FormattedMessage id="button.paymentToggle.runDetails" />
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            className={[classes.buttonElement, classes.flexAlignRight].join(' ')}
            onClick={() => redirectToSupportPortal()}
            exact={page.exact || false}
          >
            <FormattedMessage id="button.support_needed" />
          </Button>
        </Grid>
        <Grid item xs={12}>
          <ConnectionDetailsCard connection={connection} />
        </Grid>
        <Grid item xs={6}>
          <Card>
            <CardHeader
              title={<FormattedMessage id="connections.tasks.title" />}
              action={
                <>
                  <Button
                    className={classes.buttonElement}
                    color="primary"
                    variant="outlined"
                    onClick={() => clearTasks(false)}
                    disabled={clearingTasks}
                  >
                    <FormattedMessage id="connectionDetails.clearTasks" />
                  </Button>
                  <Button
                    className={classes.buttonElement}
                    color="primary"
                    variant="outlined"
                    onClick={() => clearTasks(true)}
                    disabled={clearingTasks}
                  >
                    <FormattedMessage id="connectionDetails.forceClearTasks" />
                  </Button>
                </>
              }
            />
            <CardContent>
              <TasksTable
                tasks={tasks}
                loading={loadingTasks || loading}
                paging
                page={page - 1}
                rowsPerPage={pageSize}
                totalCount={totalCount}
                handlePageChange={handlePageChange}
                handleRowsPerPageChange={handleRowsPerPageChange}
                handleRetryTask={handleRetryTask}
                connectionId={connectionId}
                isUserSuperAdmin={isUserSuperAdmin}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6}>
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title={<FormattedMessage id="connections.syncStatus.title" />}
                action={
                  <>
                    <Button
                      color="primary"
                      variant="outlined"
                      className={classes.buttonElement}
                      onClick={() => handleClickResetSyncItems(connectionId)}
                    >
                      <FormattedMessage id="button.resetSyncItems" />
                    </Button>

                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={() => retrieveSyncStatusReport()}
                      className={classes.buttonElement}
                      disabled={loadingSyncStatusReport}
                    >
                      <FormattedMessage id="connections.syncStatus.refresh" />
                    </Button>
                  </>
                }
              />

              <CardContent>
                <SyncStatusTable
                  syncStatusReport={syncStatusReport}
                  loading={loadingSyncStatusReport || loading}
                  isUserSuperAdmin={isUserSuperAdmin}
                />
              </CardContent>
            </Card>

            <Grid item xs={12} />
            <Card className={classes.content}>
              <CardHeader
                title={<FormattedMessage id="connections.logs.title" />}
                action={
                  <>
                    <Button
                      color="primary"
                      variant="outlined"
                      onClick={() => handleClickUpdateAllLogsStatus(LOG_STATUS.RESOLVED)}
                      className={classes.buttonElement}
                      disabled={updatingAllLogsStatus}
                    >
                      <FormattedMessage id="connectionDetails.markAllLogsAsResolved" />
                    </Button>

                    <Button
                      disabled={updatingSelectedLogsStatus || logs.filter(item => item.selectedRow).length === 0}
                      className={classes.buttonElement}
                      color="primary"
                      variant="outlined"
                      onClick={() => handleClickUpdateSelectedLogsStatus(LOG_STATUS.RESOLVED)}
                    >
                      <FormattedMessage id="connectionDetails.markLogsAsResolved" />
                    </Button>

                    {isUserSuperAdmin && (
                      <>
                        <Button
                          className={classes.buttonElement}
                          color="primary"
                          variant="outlined"
                          onClick={() => clearLogs(false)}
                          disabled={clearingLogs}
                        >
                          <FormattedMessage id="connectionDetails.clearLogs" />
                        </Button>
                        <Button
                          className={classes.buttonElement}
                          color="primary"
                          variant="outlined"
                          onClick={() => clearSelectedLogs()}
                          disabled={clearingSelectedLogs || logs.filter(item => item.selectedRow).length === 0}
                        >
                          <FormattedMessage id="connectionDetails.clearLogs.selected" />
                        </Button>
                      </>
                    )}
                    {/* <Tooltip */}
                    {/*  disableHoverListener={checkingDownload || canDownload} */}
                    {/*  title={<FormattedMessage id="connectionDetails.logEntries.noMissingDetails" />} */}
                    {/* > */}
                    {/*  <span> */}
                    {/*    <Button */}
                    {/*      color="primary" */}
                    {/*      variant="outlined" */}
                    {/*      onClick={() => downloadLogs()} */}
                    {/*      disabled={!canDownload} */}
                    {/*    > */}
                    {/*      <FormattedMessage id="connectionDetails.logEntries.download" /> */}
                    {/*    </Button> */}
                    {/*  </span> */}
                    {/* </Tooltip> */}
                  </>
                }
              />
              <CardContent>
                <SearchInput
                  className={classes.searchInput}
                  onChange={handleSearch}
                  placeholder={intl.formatMessage({ id: 'connections.search' })}
                />
                <LogsTable
                  logs={logs}
                  loading={loadingLogs || loading}
                  paging
                  page={pageLogs - 1}
                  rowsPerPage={pageSizeLogs}
                  totalCount={totalCountLogs}
                  handlePageChange={handlePageChangeLogs}
                  handleRowsPerPageChange={handleRowsPerPageChangeLogs}
                  handleToggleAllLogs={handleToggleAllLogs}
                  handleToggleLog={handleToggleLog}
                  handleOpenLog={handleOpenLog}
                  openedLog={openedLog}
                  isUserSuperAdmin={isUserSuperAdmin}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {modal2Open && (
        <ChangeConnectionWizard
          onClose={() => setModal2Open(false)}
          onSubmit={handleChangeConnection}
          connectionId={connectionId}
        />
      )}
    </div>
  );
};

export default ConnectionDetailsPage;
